<template>
  <div class="log-viewer">
    <div class="log-header">
      <div class="log-header-left">
        <BackButton />
        <h4>{{ $t('logs.title') }}</h4>
      </div>
    </div>

    <div v-if="showStoragePermissionBanner" class="permission-banner">
      <div class="permission-banner__content">
        <div class="permission-banner__title">{{$t('logs.needsenabled')}}</div>
        <div class="permission-banner__message">
          {{ $t('logs.storagePermissionMessage') }}
        </div>
      </div>
      <var-button type="warning" size="small" @click="openStoragePermissionGuide">
        {{ $t('logs.enableStorage') }}
      </var-button>
    </div>

    <div class="log-filter">
      <!-- 查看过滤：仅影响列表展示，不影响保存策略 -->
      <div>
        <!-- <h4>{{ $t('logs.viewRange') }}:</h4> -->
        <var-radio-group v-model="viewFilter">
          <div class="group-right">
            <div class=" radio-group">
              <var-radio checked-value="error">{{ $t('logs.error') }}</var-radio>
              <var-radio checked-value="ALL">{{ $t('logs.all') }}</var-radio>
            </div>
            <!-- </div> -->
            <div class="log-actions">
              <var-button type="primary" size="small" @click="refreshLogs">{{ $t('logs.refresh') }}</var-button>
              <var-button
                v-if="!isAndroid || storagePermissionGranted"
                type="info"
                size="small"
                @click="exportLogs"
              >
                {{ $t('logs.export') }}
              </var-button>
              <var-button type="danger" size="small" @click="clearLogs">{{ $t('logs.clear') }}</var-button>
            </div>
          </div>
        </var-radio-group>
      </div>

      <!-- 原有的五级过滤器仅用于查看，按需保留，这里注释掉
      <var-radio-group v-model="filterLevel">
        <var-radio :checked-value="''">全部</var-radio>
        <var-radio checked-value="DEBUG">调试</var-radio>
        <var-radio checked-value="INFO">信息</var-radio>
        <var-radio checked-value="WARN">警告</var-radio>
        <var-radio checked-value="ERROR">错误</var-radio>
      </var-radio-group>
      -->
    </div>

    <div class="log-content">
      <div v-if="filteredLogs.length === 0" class="no-logs">
        {{ $t('logs.noLogs') }}
      </div>
      <div v-else class="log-entries">
        <div
          v-for="(log, index) in filteredLogs"
          :key="index"
          class="log-entry"
          :class="{
            'log-debug': log.includes('[DEBUG]'),
            'log-info': log.includes('[INFO]'),
            'log-warn': log.includes('[WARN]'),
            'log-error': log.includes('[ERROR]'),
          }"
        >
          {{ log }}
        </div>
      </div>
    </div>

    <var-dialog
      v-model:show="showStoragePermissionDialog"
      :title="$t('logs.storagePermissionDialogTitle')"
      show-cancel-button
      :cancel-button-text="$t('logs.storagePermissionLater')"
      :confirm-button-text="$t('logs.enableStorage')"
      @confirm="openStoragePermissionGuide"
    >
      <div class="permission-dialog-body">
        {{ $t('logs.storagePermissionDialogBody') }}
      </div>
    </var-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { toast } from 'd3components/src/utils/tools';
import { logger, setInfoLoggingEnabled } from '@/capacitor/logger/simplelogger';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { isMiniProgramEnv ,isPC} from '@/utils/platform';
import BackButton from '@/layout/components/backbutton.vue';
import {updateKeyJson,getsubKey,} from 'd3components/src/utils/localStorage';
import {
  getAndroidStoragePermissionStatus,
  requestAndroidStoragePermission,
} from '@/capacitor/permissions/android-storage-permission';
import {
  checkAndroidD3LogsDirExists,
  ensureAndroidD3Directories,
} from '@/capacitor/permissions/android-storage-permission';


export default defineComponent({
  name: 'SimpleLogViewer',
  components: {
    BackButton,
  },
  setup() {
    const logs = ref<string[]>([]);
    const storagePermissionGranted = ref(true);
    const storagePermissionChecked = ref(false);
    const showStoragePermissionDialog = ref(false);
    const hasPromptedThisVisit = ref(false);
    // 查看过滤：'ALL' 展示全部；'error' 只展示错误
    // 控制是否记录 info 日志（默认不记录，只有选择"全部"才开启）
    const viewFilter = ref<'ALL' | 'error'>('error');

    // 记住错误/全部的选择： 写入 mobile 这个统一缓存
    const mobileFilterKey = 'view_Filter';

    const isAndroid = computed(() => Capacitor.getPlatform() === 'android');
    const showStoragePermissionBanner = computed(
      () => isAndroid.value && storagePermissionChecked.value && !storagePermissionGranted.value,
    );

    const filteredLogs = computed(() => {
      if (viewFilter.value === 'error') {
        return logs.value.filter((log) => log.includes('[ERROR]'));
      }
      return logs.value;
    });

    const ensureD3Directories = async (): Promise<boolean> => {
      if (!isAndroid.value) return true;
      try {
        if (await checkAndroidD3LogsDirExists()) return true;
        const ready = await ensureAndroidD3Directories();
        return ready || (await checkAndroidD3LogsDirExists());
      } catch (error) {
        console.error('创建 d3 目录失败:', error);
        return await checkAndroidD3LogsDirExists();
      }
    };

    const checkStoragePermission = async (promptWhenDenied = false) => {
      if (!isAndroid.value) {
        storagePermissionGranted.value = true;
        storagePermissionChecked.value = true;
        showStoragePermissionDialog.value = false;
        return true;
      }

      try {
        const status = await getAndroidStoragePermissionStatus();
        storagePermissionGranted.value = !!status.granted;
        storagePermissionChecked.value = true;

        if (status.granted) {
          showStoragePermissionDialog.value = false;
          await ensureD3Directories();
          return true;
        }

        if (promptWhenDenied && !hasPromptedThisVisit.value) {
          showStoragePermissionDialog.value = true;
          hasPromptedThisVisit.value = true;
        }
      } catch (error) {
        console.error('检查存储权限失败:', error);
        storagePermissionGranted.value = false;
        storagePermissionChecked.value = true;
      }

      return false;
    };

    const openStoragePermissionGuide = async () => {
      try {
        const status = await requestAndroidStoragePermission();
        storagePermissionGranted.value = !!status.granted;
        storagePermissionChecked.value = true;
        showStoragePermissionDialog.value = false;

        if (status.granted) {
          const dirsReady = await ensureD3Directories();
          toast(dirsReady ? '存储权限已开启' : '权限已开启，但目录创建失败', dirsReady ? 1 : 3);
          return;
        }

        // if (status.openedSettings) {
        //   toast('请在系统设置中开启存储权限', 2);
        // } else {
        //   toast('未开启存储权限，日志可能无法完整落盘', 2);
        // }
      } catch (error) {
        console.error('申请存储权限失败:', error);
        toast('申请存储权限失败', 3);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        await checkStoragePermission(false);
      }
    };

    const loadLogs = async () => {
      try {
        // HarmonyOS 平台：从原生插件读取日志文件
        // 检测 HarmonyOS: platform 为 'harmony' 或者存在 HarmonyBridge
        const isHarmonyOS = Capacitor.getPlatform() === 'harmony' || 
                           (typeof (window as any).HarmonyBridge !== 'undefined');
        
        if (isHarmonyOS && (window as any).Capacitor?.Plugins?.Logger) {
          console.log('🔍 检测到 HarmonyOS 平台，使用原生日志插件');
          const LoggerPlugin = (window as any).Capacitor.Plugins.Logger;
          
          // 获取日志文件列表
          const { files } = await LoggerPlugin.getLogFiles();
          // console.log('📁 日志文件列表:', files);
          
          if (files && files.length > 0) {
            // 读取最新的日志文件
            const latestFile = files[0];
            // console.log('📄 读取日志文件:', latestFile);
            const { content } = await LoggerPlugin.getLogFile({ fileName: latestFile });
            
            // 解析日志内容为数组
            logs.value = content.split('\n').filter((line: string) => line.trim());
            // console.log('✅ 日志加载成功，共', logs.value.length, '条');
          } else {
            console.log('⚠️ 没有找到日志文件');
            logs.value = [];
          }
        } else {
          console.log('🌐 使用内存日志（非 HarmonyOS 平台）');
          // 其他平台：使用内存中的历史日志
          const allLogs = logger.getAllLogs();
          const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
          const fmt = (d: Date) =>
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
              d.getSeconds(),
            )}`;
          logs.value = allLogs.map(({ level, message, ts }) => `[${fmt(ts)}][${level.toUpperCase()}] ${message}`);
        }
      } catch (error) {
        console.error('❌ 加载日志失败:', error);
        logs.value = [];
      }
    };
    // 日志刷新
    const refreshLogs = async () => {
      try {
        await loadLogs();
        toast('日志已刷新', 1);
      } catch (e) {
        console.error('刷新日志失败:', e);
        toast('日志刷新失败', 3);
      }
    };

    const clearLogs = async () => {
      try {
        // HarmonyOS 平台：调用原生插件清除日志
        const isHarmonyOS = Capacitor.getPlatform() === 'harmony' || 
                           (typeof (window as any).HarmonyBridge !== 'undefined');
        
        if (isHarmonyOS && (window as any).Capacitor?.Plugins?.Logger) {
          console.log('🗑️ HarmonyOS: 调用原生插件清除日志');
          const LoggerPlugin = (window as any).Capacitor.Plugins.Logger;
          await LoggerPlugin.clearAllLogs();
        } else {
          console.log('🗑️ 清除内存日志');
          // 其他平台：清除内存日志
          await logger.clearLogs();
        }
        logs.value = [];
        toast('日志已清除', 1);
      } catch (e) {
        console.error('❌ 清除日志失败:', e);
        toast('清除日志失败', 3);
      }
    };

    const exportLogs = async () => {
      try {
        const logContent = filteredLogs.value.join('\n');
        if (!logContent.trim()) {
          toast('没有日志内容可导出', 2);
          return;
        }

        // 检测 HarmonyOS 平台
        const isHarmonyOS = Capacitor.getPlatform() === 'harmony' || 
                           (typeof (window as any).HarmonyBridge !== 'undefined');
        
        if (isHarmonyOS && (window as any).Capacitor?.Plugins?.Logger) {
          // HarmonyOS: 导出到用户选择的位置
          console.log('🔍 HarmonyOS 平台，使用文件选择器导出日志');
          const LoggerPlugin = (window as any).Capacitor.Plugins.Logger;
          
          const result = await LoggerPlugin.exportLogs();
          
          if (result.success) {
            toast(`已导出日志文件到${result.targetPath}`, 1);
          } else {
            toast(`导出失败: ${result.error}`, 3);
          }
        } else {
          // 其他平台: 使用 Share API
          console.log('🌐 非 HarmonyOS 平台，使用 Share API');
          if (Capacitor.isNativePlatform()) {
            await Share.share({
              title: '应用日志',
              text: logContent,
              dialogTitle: '分享应用日志',
            });
          } else if (isMiniProgramEnv()) {
            toast('已上传', 1);
          } else if (isPC()) {
            toast('已上传', 1);
          }
        }
      } catch (error) {
        console.error('导出日志失败:', error);
        toast('导出日志失败', 2);
      }
    };

    // const purgeNonError = async () => {
    //   try {
    //     await logger.purgeNonErrorLogs();
    //     loadLogs();
    //     Snackbar.success('已清理非错误日志');
    //   } catch (e) {
    //     Snackbar.error('清理失败');
    //   }
    // };
    onMounted(async () => {
      // 调试信息：检查平台和插件状态
      // console.log(' 平台:', Capacitor.getPlatform());
      // console.log('HarmonyBridge 存在:', typeof (window as any).HarmonyBridge !== 'undefined');
      // console.log('Logger 插件存在:', typeof (window as any).Capacitor?.Plugins?.Logger !== 'undefined');
      
      // 初始为只看错误 → 关闭 info 记录
      // setInfoLoggingEnabled(false);
      // 避免“返回再进入页面时 radio 状态丢失”
      try {
        const cached = getsubKey('mobile', mobileFilterKey);
        if (cached === 'ALL' || cached === 'error') {
          viewFilter.value = cached;
        }
      } catch {}
      // 根据当前选择同步 info 记录开关（ALL -> 开；error -> 关）
      setInfoLoggingEnabled(viewFilter.value === 'ALL');
      document.addEventListener('visibilitychange', handleVisibilityChange);
      await checkStoragePermission(true);
      await loadLogs();
      // console.info('✅ 日志查看器已加载');
    });

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });

    // 监听筛选器变化：当切换为“全部”时，开启 全部 日志记录；切回“错误”时关闭
    watch(viewFilter, (val) => {
      setInfoLoggingEnabled(val === 'ALL');
      try {
        updateKeyJson('mobile', mobileFilterKey, val);
      } catch {}
    });

    return {
      logs,
      viewFilter,
      filteredLogs,
      isAndroid,
      storagePermissionGranted,
      showStoragePermissionBanner,
      showStoragePermissionDialog,
      openStoragePermissionGuide,
      refreshLogs,
      clearLogs,
      exportLogs,
      // purgeNonError
    };
  },
});
</script>

<style lang="scss" scoped>
.is-wechat .log-viewer .log-header {
  padding-left: 10px;
}
.log-viewer {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--body-color);
  overflow: hidden;

  .log-header {
    padding: 20px 35px;
    background-color: var(--body-color);
    border-bottom: 1px solid var(--color-outline);
    display: flex;
    justify-content: space-between;
    align-items: center;
   
    h3 {
      margin: 0;
      font-size: 28px;
    }
    .log-header-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .permission-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 35px;
    background: rgba(255, 193, 7, 0.12);
    border-bottom: 1px solid rgba(255, 193, 7, 0.28);

    &__content {
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-size: 15px;
      font-weight: 600;
      color: #ad6800;
      margin-bottom: 4px;
    }

    &__message {
      font-size: 13px;
      line-height: 1.5;
      color: #8c6d1f;
    }
  }

  .log-filter {
    padding: 20px 35px;
    background-color: var(--body-color);
    border-bottom: 1px solid var(--color-outline);
    display: flex;
    align-items: center;
  }
  .log-filter > div {
    width: 100%;
  }
  .log-filter :deep(.var-radio-group) {
    width: 100%;
    display: block;
  }
  .log-filter :deep(.var-radio__text) {
    color: var(--color-text) !important;
  }
  .log-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px 0;
    -webkit-overflow-scrolling: touch;
    position: relative;
    min-height: 0;

    .no-logs {
      padding: 20px;
      text-align: center;
      color: #999;
    }

    .log-entries {
      .log-entry {
        padding: 8px 15px;
        font-family: 'Courier New', monospace;
        font-size: 15px;
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-all;
        border-bottom: 1px solid var(--color-outline);
        min-height: 20px;

        &.log-debug {
          color: #6c757d;
        }

        &.log-info {
          color: #0d6efd;
        }

        &.log-warn {
          color: #ffc107;
          background-color: rgba(255, 193, 7, 0.05);
        }

        &.log-error {
          color: #dc3545;
          background-color: rgba(220, 53, 69, 0.05);
        }
      }
    }
  }
}
/* 原生透明状态栏 overlaysWebView=true 时：仅在需要适配的设备上为页面顶部预留安全区 */
:global(#app.needs-status-bar-adaptation) .log-viewer {
  padding-top: var(--safe-area-inset-top) !important;
  transition: padding-top 0.2s ease-out !important;
  box-sizing: border-box !important;
}
.log-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.group-right {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  font-size: 24px;
}
.radio-group {
  display: flex;
  gap: 16px;
}

.permission-dialog-body {
  padding: 16px 0;
  line-height: 1.7;
  color: var(--color-text);
}
</style>